import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LABELS, type CyclePhase, type DdBasis } from "@match/shared";
import { PageHeader } from "@/components/Layout";
import { PairView } from "@/components/pair";
import { PHASE_LABEL } from "@/components/session";
import { Button, ErrorNote, Field, inputClass, useCanWrite } from "@/components/ui";
import { api, errorMessage } from "@/lib/api";
import { money, percent } from "@/lib/format";
import { useAccountGroups, usePropFirms, useRefreshDirectory, useServers } from "@/lib/queries";
import { decimalInput, fractionToPct, intInput, pctToFraction } from "@/lib/units";
import type { AccountGroup, BrokerServer, Preset } from "@/lib/types";

const PHASES: CyclePhase[] = ["PHASE_1", "PHASE_2", "FUNDED"];
const BASES = Object.keys(LABELS.ddBasis) as DdBasis[];

const targetFor = (preset: Preset | undefined, phase: CyclePhase) =>
  !preset ? "" : phase === "PHASE_1" ? fractionToPct(preset.phase1TargetPct) : phase === "PHASE_2" ? fractionToPct(preset.phase2TargetPct) : "";

/**
 * Creazione di una Fase: UN form, non un wizard.
 *
 * La Fase tiene la coppia di conti, il simbolo, il ciclo e i limiti di rischio.
 * Lotto, moltiplicatore, stop loss e take profit NON stanno qui: si scelgono a
 * ogni operazione, nella scheda Operazione. Senza quei quattro campi non
 * restava abbastanza per tre schermate — e non c'e' piu' niente da proiettare
 * qui dentro, perche' la proiezione della bruciatura ha bisogno proprio di
 * quelli.
 */
export function SessionWizard() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const refresh = useRefreshDirectory();
  const groups = useAccountGroups();
  const servers = useServers();
  const firms = usePropFirms();

  // --- Coppia, fase, simbolo ---
  const [clientId, setClientId] = useState("");
  const [propAccountId, setPropAccountId] = useState("");
  const [brokerAccountId, setBrokerAccountId] = useState("");
  const [phase, setPhase] = useState<CyclePhase>("PHASE_1");
  const [cycleLabel, setCycleLabel] = useState("");
  const [symbol, setSymbol] = useState("");

  // --- Ciclo, solo se nuovo ---
  const [presetId, setPresetId] = useState("");
  const [accountSize, setAccountSize] = useState("");
  const [fee, setFee] = useState("");
  const [feeCurrency, setFeeCurrency] = useState("");
  const [feeRate, setFeeRate] = useState("");
  const [maxDdPct, setMaxDdPct] = useState("");
  const [basis, setBasis] = useState<DdBasis>("STATIC_INITIAL");
  const [desiredNet, setDesiredNet] = useState("0");

  // --- Limiti della fase ---
  const [dailyDdPct, setDailyDdPct] = useState("");
  const [targetPct, setTargetPct] = useState("");
  const [maxOps, setMaxOps] = useState("1");

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const group = groups.data?.find((g) => g.client.id === clientId);
  const cycle = group?.cycle ?? null;
  const propAccounts = group?.accounts.filter((a) => a.role === "PROP") ?? [];
  const brokerAccounts = group?.accounts.filter((a) => a.role === "BROKER") ?? [];
  const prop = propAccounts.find((a) => a.id === propAccountId) ?? null;
  const broker = brokerAccounts.find((a) => a.id === brokerAccountId) ?? null;
  const allPresets = (firms.data ?? []).flatMap((f) => f.presets.map((p) => ({ ...p, firmName: f.name })));
  const preset = allPresets.find((p) => p.id === (cycle ? cycle.preset.id : presetId));

  // Scelto il cliente: conti più recenti, fase e preset del ciclo attivo se c'è.
  useEffect(() => {
    if (!group) return;
    setPropAccountId(group.pair.prop?.id ?? propAccounts[0]?.id ?? "");
    setBrokerAccountId(group.pair.broker?.id ?? brokerAccounts[0]?.id ?? "");
    setSymbol("");
    if (group.cycle) setPhase(group.cycle.currentPhase);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Scelto il preset (o trovato il ciclo): si precompila.
  useEffect(() => {
    if (!preset) return;
    if (!cycle) {
      setAccountSize(String(Number(preset.accountSize)));
      setFee(String(Number(preset.challengeFee)));
      setMaxDdPct(fractionToPct(preset.maxDdPct));
      setBasis(preset.maxDdBasis);
    }
    setDailyDdPct(fractionToPct(preset.dailyDdPct));
    setTargetPct(targetFor(preset, phase));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.id, cycle?.id]);

  useEffect(() => {
    if (preset) setTargetPct(targetFor(preset, phase));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Valori effettivi: con un ciclo attivo valgono i suoi.
  const effective = {
    accountSize: cycle ? String(Number(cycle.accountSize)) : decimalInput(accountSize),
    maxDdPct: cycle ? cycle.maxDdPct : pctToFraction(maxDdPct),
    basis: cycle ? cycle.maxDdBasis : basis,
    feesPaid: cycle ? String(Number(cycle.feesPaid)) : decimalInput(fee),
  };

  const problems = [
    !prop && "conto prop",
    !broker && "conto broker",
    !symbol && "simbolo",
    !cycle && !presetId && "preset della prop firm",
    !effective.accountSize && "taglia del conto",
    !effective.maxDdPct && "max drawdown",
    !effective.feesPaid && "fee",
    !intInput(maxOps) && "operazioni al giorno",
    dailyDdPct.trim() && !pctToFraction(dailyDdPct) && "DD giornaliero",
    targetPct.trim() && !pctToFraction(targetPct) && "target di fase",
  ].filter(Boolean) as string[];

  const create = async () => {
    if (problems.length || !prop || !broker) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.sessions.create({
        clientId,
        propAccountId: prop.id,
        brokerAccountId: broker.id,
        canonicalSymbol: symbol,
        phase,
        ...(!cycle && {
          presetId,
          cycleLabel: cycleLabel.trim() || null,
          accountSize: effective.accountSize!,
          challengeFee: effective.feesPaid!,
          challengeFeeCurrency: feeCurrency || prop.currency,
          ...(decimalInput(feeRate) ? { challengeFeeFxRate: decimalInput(feeRate)! } : {}),
          maxDdPct: effective.maxDdPct!,
          maxDdBasis: effective.basis,
          desiredNetProfit: decimalInput(desiredNet) ?? "0",
        }),
        dailyDdPct: dailyDdPct.trim() ? pctToFraction(dailyDdPct) : null,
        challengeTargetPct: targetPct.trim() ? pctToFraction(targetPct) : null,
        maxOpsPerDay: intInput(maxOps) ?? 1,
      });
      await refresh();
      navigate(`/fasi/${r.session.id}`);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!canWrite) {
    return (
      <>
        <PageHeader title="Nuova fase" />
        <div className="px-6 py-6">
          <ErrorNote>Il tuo ruolo non può creare fasi.</ErrorNote>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Nuova fase" subtitle={group ? group.client.name : "Coppia di conti, ciclo, limiti"} />

      <div className="max-w-5xl space-y-6 px-6 py-5">
        <ClientSection
          groups={groups.data ?? []}
          servers={servers.data ?? []}
          clientId={clientId}
          setClientId={setClientId}
          group={group}
          propAccountId={propAccountId}
          setPropAccountId={setPropAccountId}
          brokerAccountId={brokerAccountId}
          setBrokerAccountId={setBrokerAccountId}
          phase={phase}
          setPhase={setPhase}
          cycleLabel={cycleLabel}
          setCycleLabel={setCycleLabel}
          symbol={symbol}
          setSymbol={setSymbol}
        />

        {group && prop && broker && (
          <div className="grid gap-6 lg:grid-cols-2">
            <section className="space-y-4">
              <h2 className="text-sm font-medium text-ink">{cycle ? "Ciclo attivo" : "Prop firm e ciclo"}</h2>
              {cycle ? (
                <dl className="grid grid-cols-2 gap-3 rounded-card border border-line bg-surface p-4 text-sm">
                  <Info label="Preset">{`${cycle.propFirm.name} ${cycle.preset.name}`}</Info>
                  <Info label="Fase">{PHASE_LABEL(cycle.currentPhase)}</Info>
                  <Info label="Taglia">{money(cycle.accountSize, prop.currency)}</Info>
                  <Info label="Max DD">{`${percent(cycle.maxDdPct)} · ${LABELS.ddBasis[cycle.maxDdBasis]}`}</Info>
                  <Info label="Fee pagate">{money(cycle.feesPaid, cycle.currency)}</Info>
                  <Info label={LABELS.terms.recoverAmount}>{money(cycle.recoverAmount, cycle.currency)}</Info>
                  <p className="col-span-2 text-xs text-faint">I parametri del ciclo sono fissati all'apertura: la fase li riusa.</p>
                </dl>
              ) : (
                <>
                  <Field label="Preset">
                    {(id) => (
                      <select id={id} value={presetId} onChange={(e) => setPresetId(e.target.value)} className={inputClass}>
                        <option value="" disabled>
                          Scegli prop firm e modello
                        </option>
                        {(firms.data ?? []).map((f) => (
                          <optgroup key={f.id} label={f.name}>
                            {f.presets.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name} · {p.phases === 0 ? "instant" : p.phases === 1 ? "one-step" : "two-step"}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    )}
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <NumField label="Taglia del conto" value={accountSize} onChange={setAccountSize} suffix={prop.currency} />
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <NumField label="Fee della challenge" value={fee} onChange={setFee} hint="Entra nel registro del ciclo, convertita nella valuta di reporting." />
                      </div>
                      <Field label="Valuta">
                        {(id) => <input id={id} value={feeCurrency || prop.currency} maxLength={3} onChange={(e) => setFeeCurrency(e.target.value.toUpperCase())} className={inputClass} />}
                      </Field>
                    </div>
                    <NumField label="Max drawdown" value={maxDdPct} onChange={setMaxDdPct} suffix="%" />
                    <Field label="Base di calcolo del DD">
                      {(id) => (
                        <select id={id} value={basis} onChange={(e) => setBasis(e.target.value as DdBasis)} className={inputClass}>
                          {BASES.map((b) => (
                            <option key={b} value={b}>
                              {LABELS.ddBasis[b]}
                            </option>
                          ))}
                        </select>
                      )}
                    </Field>
                  </div>
                  <NumField
                    label={`Profitto netto desiderato sul ciclo, in ${prop.currency}`}
                    value={desiredNet}
                    onChange={setDesiredNet}
                    hint={`Valuta del conto prop, la stessa del ciclo e della fee: con la fee forma il valore ${LABELS.terms.recoverAmount.toLowerCase()} della prima fase.`}
                  />
                </>
              )}
            </section>

            <section className="space-y-4">
              <h2 className="text-sm font-medium text-ink">Limiti della fase</h2>
              <p className="text-sm text-muted">
                Valgono su tutte le operazioni. Lotto, moltiplicatore, stop loss e take profit non stanno qui: si scelgono a ogni operazione.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="Operazioni massime al giorno" value={maxOps} onChange={setMaxOps} />
                <NumField label="DD giornaliero" value={dailyDdPct} onChange={setDailyDdPct} suffix="%" hint="Vuoto se la prop non lo prevede." />
                <NumField label={`Target ${PHASE_LABEL(phase)}`} value={targetPct} onChange={setTargetPct} suffix="%" hint="Vuoto in funded." />
              </div>
            </section>
          </div>
        )}

        <ErrorNote>{error}</ErrorNote>
        {/Tasso .* non disponibile/.test(error ?? "") && (
          <div className="max-w-xs">
            <Field label="Tasso della fee" hint="Né feed del broker né BCE disponibili: scrivi il tasso da congelare sulla fee, poi crea di nuovo.">
              {(id) => <input id={id} inputMode="decimal" value={feeRate} placeholder="es. 0.9200" onChange={(e) => setFeeRate(e.target.value)} className={inputClass} />}
            </Field>
          </div>
        )}

        <div className="flex items-center justify-between border-t border-line pt-4">
          <Button variant="ghost" onClick={() => navigate("/fasi")}>
            Annulla
          </Button>
          <div className="flex items-center gap-3">
            {problems.length > 0 && <span className="text-xs text-muted">Manca: {problems.join(", ")}</span>}
            <Button variant="primary" disabled={busy || problems.length > 0} onClick={() => void create()}>
              {busy ? "Creazione…" : "Crea fase"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------

function ClientSection(props: {
  groups: AccountGroup[];
  servers: BrokerServer[];
  clientId: string;
  setClientId: (v: string) => void;
  group: AccountGroup | undefined;
  propAccountId: string;
  setPropAccountId: (v: string) => void;
  brokerAccountId: string;
  setBrokerAccountId: (v: string) => void;
  phase: CyclePhase;
  setPhase: (v: CyclePhase) => void;
  cycleLabel: string;
  setCycleLabel: (v: string) => void;
  symbol: string;
  setSymbol: (v: string) => void;
}) {
  const { groups, servers, group } = props;
  const reason = (g: AccountGroup) => {
    const hasProp = g.accounts.some((a) => a.role === "PROP");
    const hasBroker = g.accounts.some((a) => a.role === "BROKER");
    if (!hasProp && !hasBroker) return "senza conti";
    if (!hasProp) return "manca il conto prop";
    if (!hasBroker) return "manca il conto broker";
    if (g.cycle?.session) return "ha già una fase aperta";
    return null;
  };

  const prop = group?.accounts.find((a) => a.id === props.propAccountId) ?? null;
  const broker = group?.accounts.find((a) => a.id === props.brokerAccountId) ?? null;
  const propServer = servers.find((s) => s.id === prop?.serverId);
  const brokerServer = servers.find((s) => s.id === broker?.serverId);

  const symbolState = useMemo(() => {
    if (!propServer || !brokerServer) return null;
    const onProp = new Set(propServer.symbols.map((s) => s.canonical));
    const onBroker = new Set(brokerServer.symbols.map((s) => s.canonical));
    return {
      both: [...onProp].filter((c) => onBroker.has(c)).sort(),
      onlyProp: [...onProp].filter((c) => !onBroker.has(c)).sort(),
      onlyBroker: [...onBroker].filter((c) => !onProp.has(c)).sort(),
    };
  }, [propServer, brokerServer]);

  const serverName = (s: BrokerServer) => s.displayName ?? s.name;
  const link = (s: BrokerServer, canonical?: string) => `/server?server=${s.id}${canonical ? `&symbol=${encodeURIComponent(canonical)}` : ""}`;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <Field label="Cliente" hint={groups.length === 0 ? "Nessun cliente: creane uno dalla pagina Clienti." : undefined}>
          {(id) => (
            <select id={id} value={props.clientId} onChange={(e) => props.setClientId(e.target.value)} className={inputClass}>
              <option value="" disabled>
                Scegli il cliente
              </option>
              {groups.map((g) => {
                const why = reason(g);
                return (
                  <option key={g.client.id} value={g.client.id} disabled={!!why}>
                    {g.client.name}
                    {why ? ` · ${why}` : g.cycle ? ` · ciclo in ${PHASE_LABEL(g.cycle.currentPhase)}` : ""}
                  </option>
                );
              })}
            </select>
          )}
        </Field>
        <Field
          label="Fase"
          hint={group?.cycle ? `Il ciclo attivo è in ${PHASE_LABEL(group.cycle.currentPhase)}: la nuova fase è questa.` : "Un ciclo nuovo parte di norma dalla fase 1."}
        >
          {(id) => (
            <select id={id} value={props.phase} disabled={!!group?.cycle} onChange={(e) => props.setPhase(e.target.value as CyclePhase)} className={inputClass}>
              {PHASES.map((p) => (
                <option key={p} value={p}>
                  {PHASE_LABEL(p)}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>

      {group && (
        <>
          {(group.accounts.filter((a) => a.role === "PROP").length > 1 || group.accounts.filter((a) => a.role === "BROKER").length > 1) && (
            <div className="grid gap-4 md:grid-cols-2">
              <Field label="Conto prop">
                {(id) => (
                  <select id={id} value={props.propAccountId} onChange={(e) => props.setPropAccountId(e.target.value)} className={inputClass}>
                    {group.accounts.filter((a) => a.role === "PROP").map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label} · {a.mt5Login}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="Conto broker">
                {(id) => (
                  <select id={id} value={props.brokerAccountId} onChange={(e) => props.setBrokerAccountId(e.target.value)} className={inputClass}>
                    {group.accounts.filter((a) => a.role === "BROKER").map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.label} · {a.mt5Login}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>
          )}

          <div>
            <p className="mb-2 text-sm text-muted">La coppia che userà la fase</p>
            <PairView prop={prop} broker={broker} canWrite={false} compact />
            {(prop && !prop.isConnected) || (broker && !broker.isConnected) ? (
              <p className="mt-2 text-xs text-warn">
                Puoi creare la fase anche con i conti disconnessi, ma per aprire operazioni vanno connessi.{" "}
                <Link to={`/clienti/${group.client.id}`} className="underline">
                  Connettili dal dettaglio cliente
                </Link>
              </p>
            ) : null}
          </div>

          {!group.cycle && (
            <Field label="Etichetta del ciclo" hint="Facoltativa, per riconoscerlo nello storico: per esempio FundedNext 100k #1.">
              {(id) => <input id={id} value={props.cycleLabel} onChange={(e) => props.setCycleLabel(e.target.value)} className={inputClass} />}
            </Field>
          )}

          <Field label="Simbolo">
            {(id) =>
              !symbolState || !propServer || !brokerServer ? (
                <p id={id} className="text-sm text-muted">
                  Caricamento dei server…
                </p>
              ) : symbolState.both.length ? (
                <>
                  <select id={id} value={props.symbol} onChange={(e) => props.setSymbol(e.target.value)} className={inputClass}>
                    <option value="" disabled>
                      Scegli il simbolo
                    </option>
                    {symbolState.both.map((c) => (
                      <option key={c} value={c}>
                        {c} · {propServer.symbols.find((s) => s.canonical === c)?.brokerSymbol} / {brokerServer.symbols.find((s) => s.canonical === c)?.brokerSymbol}
                      </option>
                    ))}
                  </select>
                  <MissingSymbols state={symbolState} propServer={propServer} brokerServer={brokerServer} serverName={serverName} link={link} compact />
                </>
              ) : (
                <div id={id} className="rounded-card border border-warn/40 bg-warn/10 p-3 text-sm text-ink">
                  <p>Nessun simbolo è mappato su entrambi i server: la fase non può calcolare i lotti delle due gambe.</p>
                  <MissingSymbols state={symbolState} propServer={propServer} brokerServer={brokerServer} serverName={serverName} link={link} />
                </div>
              )
            }
          </Field>
        </>
      )}
    </div>
  );
}

function MissingSymbols({
  state,
  propServer,
  brokerServer,
  serverName,
  link,
  compact = false,
}: {
  state: { both: string[]; onlyProp: string[]; onlyBroker: string[] };
  propServer: BrokerServer;
  brokerServer: BrokerServer;
  serverName: (s: BrokerServer) => string;
  link: (s: BrokerServer, canonical?: string) => string;
  compact?: boolean;
}) {
  const lines: ReactNode[] = [];
  for (const c of state.onlyProp) {
    lines.push(
      <li key={`p-${c}`}>
        {c} è mappato su <span className="text-prop">{serverName(propServer)}</span> ma non su <span className="text-broker">{serverName(brokerServer)}</span>.{" "}
        <Link to={link(brokerServer, c)} className="underline">
          Mappalo sul server broker
        </Link>
      </li>,
    );
  }
  for (const c of state.onlyBroker) {
    lines.push(
      <li key={`b-${c}`}>
        {c} è mappato su <span className="text-broker">{serverName(brokerServer)}</span> ma non su <span className="text-prop">{serverName(propServer)}</span>.{" "}
        <Link to={link(propServer, c)} className="underline">
          Mappalo sul server prop
        </Link>
      </li>,
    );
  }
  if (!state.both.length && !lines.length) {
    lines.push(
      <li key="none">
        Nessun simbolo mappato né su <span className="text-prop">{serverName(propServer)}</span> né su{" "}
        <span className="text-broker">{serverName(brokerServer)}</span>.{" "}
        <Link to={link(propServer)} className="underline">
          Vai alla pagina Server
        </Link>
      </li>,
    );
  }
  if (!lines.length) return null;
  return <ul className={`${compact ? "mt-2 text-xs text-muted" : "mt-2"} space-y-1`}>{lines}</ul>;
}

function NumField({ label, value, onChange, suffix, hint }: { label: string; value: string; onChange: (v: string) => void; suffix?: string; hint?: string }) {
  return (
    <Field label={label} hint={hint}>
      {(id) => (
        <div className="relative">
          <input id={id} inputMode="decimal" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputClass} ${suffix ? "pr-10" : ""}`} />
          {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>}
        </div>
      )}
    </Field>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs text-faint">{label}</dt>
      <dd className="mt-0.5 text-ink">{children}</dd>
    </div>
  );
}

/** Sempre accanto al moltiplicatore: quanto rende la bruciatura e quante operazioni servono. */
